import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  alpha,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Assessment as AssessmentIcon,
  Add as AddIcon,
  TrendingUp as TrendingUpIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import PageWithHeader from '../common/PageWithHeader';
import LoadingCover from '../common/LoadingCover';
import { useAppSelector } from '../../redux/hooks';
import { useGetMyBSCSubmissionsQuery } from '../../redux/balancedScorecardApi';
import { useNavigate } from 'react-router-dom';
import { BalancedScorecard, BSCPerspectiveScore, BSCKRAScore, PERFORMANCE_RATINGS, getBonusEntitlementFromScore } from './bscTypes';

// Helper to get status color
const getStatusColor = (status: string): 'default' | 'warning' | 'success' | 'info' | 'error' => {
  switch (status?.toLowerCase()) {
    case 'draft':
      return 'default';
    case 'submitted':
      return 'info';
    case 'under_review':
      return 'warning';
    case 'approved':
      return 'success';
    case 'rejected':
      return 'error';
    default:
      return 'default';
  }
};

// Helper to get grade color
const getGradeColor = (grade: string): string => {
  const rating = PERFORMANCE_RATINGS.find(r => r.grade === grade);
  return rating?.colorCode || '#9e9e9e';
};

// Helper to get correct bonus entitlement from score (using new bonus thresholds)
const getBonusFromScore = (score: number): number => {
  return getBonusEntitlementFromScore(score);
};

// Helper to format date
const formatDate = (dateStr?: string): string => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// Helper to format numbers with thousand separators
const formatNumber = (value: number | undefined): string => {
  if (value === undefined || value === null || isNaN(value)) return '-';
  return value.toLocaleString('en-US');
};

// Helper to get perspective color
const getPerspectiveColor = (name: string): string => {
  switch (name) {
    case 'Financial': return '#1976d2';
    case 'Customer': return '#388e3c';
    case 'Internal Processes': return '#f57c00';
    case 'Learning & Growth': return '#7b1fa2';
    default: return '#757575';
  }
};

// Read-only KRA Card Component
interface ReadOnlyKRACardProps {
  kra: BSCKRAScore;
  kraIndex: number;
  perspectiveColor: string;
}

const ReadOnlyKRACard: React.FC<ReadOnlyKRACardProps> = ({ kra, kraIndex, perspectiveColor }) => {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        mb: 2,
        borderLeft: `4px solid ${perspectiveColor}`,
        backgroundColor: kra.isExtraDuty ? 'rgba(255, 215, 0, 0.05)' : 'transparent',
      }}
    >
      {/* KRA Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle1" fontWeight="bold">
            Goal {kraIndex + 1}
          </Typography>
          {kra.isExtraDuty && (
            <Chip
              size="small"
              icon={<StarIcon sx={{ fontSize: 16 }} />}
              label="Extra Duty"
              color="warning"
              variant="outlined"
            />
          )}
        </Stack>
        <Chip
          label={`Weight: ${kra.weight}%`}
          size="small"
          sx={{ backgroundColor: perspectiveColor, color: 'white' }}
        />
      </Box>

      {/* Goal & Measure */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <Typography variant="caption" color="text.secondary" display="block">
            Goal
          </Typography>
          <Typography variant="body1">{kra.objective || '-'}</Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="caption" color="text.secondary" display="block">
            Measure
          </Typography>
          <Typography variant="body1">{kra.measure || '-'}</Typography>
        </Grid>
      </Grid>

      {/* Target & Actual Values */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={3}>
          <Typography variant="caption" color="text.secondary" display="block">
            Unit
          </Typography>
          <Typography variant="body2">
            {kra.unitOfMeasure === 'currency' ? 'USD' : 
             kra.unitOfMeasure === 'percentage' ? '%' : 
             kra.unitOfMeasure === 'number' ? 'Number' : kra.unitOfMeasure || '-'}
          </Typography>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Typography variant="caption" color="text.secondary" display="block">
            Direction
          </Typography>
          <Typography variant="body2">
            {kra.measurementDirection === 'minimize' ? '↓ Lower is better' : '↑ Higher is better'}
          </Typography>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Typography variant="caption" color="text.secondary" display="block">
            Target
          </Typography>
          <Typography variant="body2" fontWeight="medium">
            {kra.unitOfMeasure === 'currency' && '$'}
            {formatNumber(kra.targetValue)}
            {kra.unitOfMeasure === 'percentage' && '%'}
          </Typography>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Typography variant="caption" color="text.secondary" display="block">
            Actual
          </Typography>
          <Typography variant="body2" fontWeight="medium">
            {kra.unitOfMeasure === 'currency' && '$'}
            {formatNumber(kra.actualValue)}
            {kra.unitOfMeasure === 'percentage' && '%'}
          </Typography>
        </Grid>
      </Grid>

      {/* Calculated Results */}
      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          backgroundColor: 'grey.50',
          display: 'flex',
          justifyContent: 'space-around',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Performance
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: (kra.performance || 0) >= 100 ? 'success.main' : (kra.performance || 0) >= 80 ? 'warning.main' : 'error.main',
            }}
          >
            {(kra.performance || 0).toFixed(1)}%
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Score
          </Typography>
          <Typography variant="h6">{((kra.score || 0) / 100).toFixed(3)}</Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Weighted Score
          </Typography>
          <Typography variant="h6" color="primary">
            {(kra.weightedScore || 0).toFixed(2)}%
          </Typography>
        </Box>
      </Paper>
    </Paper>
  );
};

// Read-only Perspective Section Component
interface ReadOnlyPerspectiveSectionProps {
  perspective: BSCPerspectiveScore;
}

const ReadOnlyPerspectiveSection: React.FC<ReadOnlyPerspectiveSectionProps> = ({ perspective }) => {
  const [expanded, setExpanded] = useState(true);
  const perspectiveColor = getPerspectiveColor(perspective.name);
  const totalKRAWeight = perspective.kraScores?.reduce((sum, kra) => sum + (kra.weight || 0), 0) || 0;

  return (
    <Card sx={{ mb: 3, border: `2px solid ${perspectiveColor}` }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          backgroundColor: perspectiveColor,
          color: 'white',
          cursor: 'pointer',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <Typography variant="h6" sx={{ color: 'white' }}>{perspective.name}</Typography>
          <Chip
            label={`Weight: ${perspective.weight}%`}
            size="small"
            sx={{ backgroundColor: 'white', color: perspectiveColor }}
          />
          <Chip
            label={`Goal Weight: ${totalKRAWeight}%`}
            size="small"
            sx={{ backgroundColor: 'rgba(255,255,255,0.9)', color: perspectiveColor }}
          />
          <Chip
            label={`Total: ${(perspective.perspectiveTotal || 0).toFixed(2)}%`}
            size="small"
            sx={{ backgroundColor: 'rgba(255,255,255,0.9)', color: perspectiveColor }}
          />
        </Stack>
        <IconButton sx={{ color: 'white' }}>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <CardContent>
          {!perspective.kraScores || perspective.kraScores.length === 0 ? (
            <Typography color="text.secondary" textAlign="center" py={2}>
              No KRAs defined for this perspective
            </Typography>
          ) : (
            perspective.kraScores.map((kra, kraIndex) => (
              <ReadOnlyKRACard
                key={kraIndex}
                kra={kra}
                kraIndex={kraIndex}
                perspectiveColor={perspectiveColor}
              />
            ))
          )}
        </CardContent>
      </Collapse>
    </Card>
  );
};

const MyBSCSubmissions: React.FC = () => {
  const navigate = useNavigate();
  const currentUser = useAppSelector((state) => state.auth.user);
  const { data: submissions, isLoading, error } = useGetMyBSCSubmissionsQuery();
  const [selectedPeriod, setSelectedPeriod] = useState<string>('');

  if (isLoading) return <LoadingCover />;

  if (error) {
    return (
      <PageWithHeader>
        <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="error">Error loading BSC submissions</Typography>
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/reviews/bsc')}
              sx={{ mt: 2 }}
            >
              Back to BSC
            </Button>
          </Paper>
        </Box>
      </PageWithHeader>
    );
  }

  const handlePeriodChange = (event: SelectChangeEvent) => {
    setSelectedPeriod(event.target.value);
  };

  const selectedSubmission = selectedPeriod
    ? submissions?.find((bsc) => bsc.reviewPeriod === selectedPeriod)
    : null;

  // Get unique periods for dropdown
  const periods = [...new Set(submissions?.map((bsc) => bsc.reviewPeriod) || [])];

  return (
    <PageWithHeader>
      <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, md: 3 } }}>
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            mb: 3,
            borderRadius: 4,
            background: (theme) =>
              `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/reviews/bsc')}
                variant="outlined"
              >
                Back
              </Button>
              <Box>
                <Typography variant="h4" fontWeight="bold" color="primary.dark">
                  My BSC Submissions
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  View all your submitted Balanced Scorecards
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/reviews/bsc')}
            >
              New BSC
            </Button>
          </Box>
        </Paper>

        {/* Summary Stats */}
        {submissions && submissions.length > 0 && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} md={3}>
              <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                <Typography variant="h4" fontWeight="bold" color="primary">
                  {submissions.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Submissions
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={3}>
              <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                <Typography variant="h4" fontWeight="bold" color="success.main">
                  {submissions.filter((s) => s.status === 'approved').length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Approved
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={3}>
              <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                <Typography variant="h4" fontWeight="bold" color="warning.main">
                  {submissions.filter((s) => s.status === 'submitted' || s.status === 'under_review').length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pending Review
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={3}>
              <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                <Typography variant="h4" fontWeight="bold" color="info.main">
                  {submissions[0]?.finalGrade || '-'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Latest Grade
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* Period Selector */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Select Review Period</InputLabel>
            <Select
              value={selectedPeriod}
              onChange={handlePeriodChange}
              label="Select Review Period"
            >
              <MenuItem value="">
                <em>Select a period to view details</em>
              </MenuItem>
              {periods.map((period) => (
                <MenuItem key={period} value={period}>
                  {period}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>

        {/* No submissions */}
        {(!submissions || submissions.length === 0) && (
          <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
            <AssessmentIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No BSC Submissions Yet
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              You haven't submitted any Balanced Scorecards yet. Create your first one!
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/reviews/bsc')}
            >
              Create BSC
            </Button>
          </Paper>
        )}

        {/* Submissions List */}
        {submissions && submissions.length > 0 && !selectedPeriod && (
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell><strong>Period</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Score</strong></TableCell>
                  <TableCell><strong>Grade</strong></TableCell>
                  <TableCell><strong>Submitted</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {submissions.map((bsc) => (
                  <TableRow
                    key={bsc.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setSelectedPeriod(bsc.reviewPeriod)}
                  >
                    <TableCell>
                      <Typography fontWeight="medium">{bsc.reviewPeriod}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={bsc.status?.replace('_', ' ').toUpperCase() || 'DRAFT'}
                        color={getStatusColor(bsc.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography>
                        {bsc.totalWeightedScore?.toFixed(1) || '-'}%
                        {bsc.rawTotalWeightedScore && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            (capped)
                          </Typography>
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={bsc.finalGrade || '-'}
                        size="small"
                        sx={{
                          bgcolor: getGradeColor(bsc.finalGrade || ''),
                          color: 'white',
                          fontWeight: 'bold',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {formatDate(bsc.submittedAt || bsc.createdAt)}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedPeriod(bsc.reviewPeriod);
                        }}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Selected Submission Details - Full BSC View */}
        {selectedSubmission && (
          <Box>
            {/* Back to list button */}
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => setSelectedPeriod('')}
              sx={{ mb: 2 }}
            >
              Back to List
            </Button>

            {/* Header Card */}
            <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                <Box>
                  <Typography variant="h4" fontWeight="bold" color="primary">
                    {selectedSubmission.reviewPeriod}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedSubmission.jobDescriptionTitle || 'Job Description'}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip
                    label={selectedSubmission.status?.replace('_', ' ').toUpperCase() || 'DRAFT'}
                    color={getStatusColor(selectedSubmission.status)}
                  />
                  <Chip
                    label={`Grade: ${selectedSubmission.finalGrade || '-'}`}
                    sx={{
                      bgcolor: getGradeColor(selectedSubmission.finalGrade || ''),
                      color: 'white',
                      fontWeight: 'bold',
                    }}
                  />
                </Stack>
              </Box>
            </Paper>

            {/* Score Summary */}
            <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Performance Summary
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50', textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Total Score
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" color="primary">
                      {(selectedSubmission.managerAdjustedScore ?? selectedSubmission.totalWeightedScore)?.toFixed(1) || '-'}%
                    </Typography>
                    {selectedSubmission.managerAdjustedScore !== undefined && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Computed: {selectedSubmission.totalWeightedScore?.toFixed(1)}% (Manager adjusted)
                      </Typography>
                    )}
                    {!selectedSubmission.managerAdjustedScore && selectedSubmission.rawTotalWeightedScore && (
                      <Typography variant="caption" color="text.secondary">
                        Raw: {selectedSubmission.rawTotalWeightedScore.toFixed(1)}% (capped at 110%)
                      </Typography>
                    )}
                  </Paper>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50', textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Bonus Entitlement
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" color="success.main">
                      {getBonusFromScore(selectedSubmission.managerAdjustedScore ?? selectedSubmission.totalWeightedScore ?? 0)}%
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50', textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Submitted
                    </Typography>
                    <Typography variant="h6" fontWeight="medium">
                      {formatDate(selectedSubmission.submittedAt || selectedSubmission.createdAt)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50', textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Reviewed
                    </Typography>
                    <Typography variant="h6" fontWeight="medium">
                      {selectedSubmission.reviewedAt ? formatDate(selectedSubmission.reviewedAt) : 'Pending'}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
              {/* Manager Adjustment Notice */}
              {selectedSubmission.managerAdjustedScore !== undefined && (
                <Paper sx={{ mt: 2, p: 2, bgcolor: '#fff3e0', border: '1px solid #f57c00', borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ color: '#f57c00', mb: 1 }}>
                    📊 Score Adjusted by Manager
                  </Typography>
                  {selectedSubmission.managerAdjustmentNotes && (
                    <Typography variant="body2">
                      {selectedSubmission.managerAdjustmentNotes}
                    </Typography>
                  )}
                </Paper>
              )}
            </Paper>

            {/* Perspectives with KRAs */}
            {selectedSubmission.perspectives?.map((perspective, index) => (
              <ReadOnlyPerspectiveSection key={index} perspective={perspective} />
            ))}

            {/* Comments Section */}
            <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Comments
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Your Comments
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, minHeight: 80 }}>
                    <Typography>
                      {selectedSubmission.employeeComments || 'No comments provided'}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Manager Comments
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, minHeight: 80 }}>
                    <Typography>
                      {selectedSubmission.managerComments || 'No manager comments yet'}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Paper>
          </Box>
        )}
      </Box>
    </PageWithHeader>
  );
};

export default MyBSCSubmissions;
